import { HttpClient } from '@angular/common/http';
import { inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  defer,
  finalize,
  map,
  Observable,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

import { environment } from '../../../environments/environment';
import { keys } from '../../constants/constants';
import { angRoutes, apiRoutes } from '../../constants/router';
import { AuthTokens, LoginPayload, LoginResponse } from '../../models/auth';
import { GenericResponse } from '../../models/global';
import { UserAccount } from '../../models/user-account';
import { toUserPermissions } from '../../utils/permissions/permissions';
import { unwrapResponse } from '../api/api.service';
import { PermissionsService } from '../permissions/permissions.service';
import { StorageService } from '../storage/storage.service';
import { FirebaseSignInService } from './firebase-sign-in.service';

const ACTIVITY_EVENTS = ['click', 'keydown', 'scroll'] as const;

@Injectable({ providedIn: 'root' })
export class AccessService {
  private readonly firebase = inject(FirebaseSignInService);
  private readonly http = inject(HttpClient);
  private readonly permissionsService = inject(PermissionsService);
  private readonly storage = inject(StorageService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);

  private readonly isDialogBoxActiveSubject = new BehaviorSubject<boolean>(false);
  private readonly logoutConfirmationSubject = new BehaviorSubject<boolean>(false);
  private readonly currentUserSubject = new BehaviorSubject<UserAccount | null>(
    this.storage.get<UserAccount>(keys.user)
  );

  readonly isDialogBoxActive$: Observable<boolean> = this.isDialogBoxActiveSubject.asObservable();
  readonly logoutConfirmation$: Observable<boolean> = this.logoutConfirmationSubject.asObservable();
  readonly currentUser$: Observable<UserAccount | null> = this.currentUserSubject.asObservable();

  private refreshing$: Observable<string> | null = null;
  private inactivityTimeout: ReturnType<typeof setTimeout> | null = null;
  private dialogTimeout: ReturnType<typeof setTimeout> | null = null;
  private tracking = false;

  private readonly onActivity = (): void => this.resetTimer();

  login(email: string, password: string): Observable<UserAccount> {
    return defer(() => this.firebase.idToken(email, password)).pipe(
      switchMap(idToken =>
        this.http.post<GenericResponse<LoginResponse>>(this.url(apiRoutes.login), {
          email,
          idToken,
        } satisfies LoginPayload)
      ),
      map(unwrapResponse),
      tap(response => this.setSession(response)),
      map(response => response.user),
      finalize(() => this.firebase.signOut())
    );
  }

  // Shared by concurrent callers: the backend revokes a refresh token once it has been used.
  refreshSession(): Observable<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token is available for this session.'));
    }

    this.refreshing$ ??= this.http
      .post<GenericResponse<AuthTokens>>(this.url(apiRoutes.refreshToken), { refreshToken })
      .pipe(
        map(unwrapResponse),
        tap(tokens => this.storeTokens(tokens)),
        map(tokens => tokens.accessToken),
        finalize(() => (this.refreshing$ = null)),
        shareReplay(1)
      );
    return this.refreshing$;
  }

  logout(): void {
    const hadSession = this.isLoggedIn();

    this.stopTracking();
    this.isDialogBoxActiveSubject.next(false);
    this.logoutConfirmationSubject.next(false);

    if (hadSession) {
      this.http
        .post<GenericResponse<unknown>>(this.url(apiRoutes.logout), {})
        .subscribe({ error: () => undefined });
    }

    this.clearStoredSession();
    this.permissionsService.clear();
    this.currentUserSubject.next(null);
    this.router.navigate([angRoutes.login.route]).catch(() => undefined);
  }

  isLoggedIn(): boolean {
    return !!this.getJWT() && this.getCurrentUser() !== null;
  }

  getJWT(): string | null {
    return this.storage.getRaw(keys.token);
  }

  getRefreshToken(): string | null {
    return this.storage.getRaw(keys.refreshToken);
  }

  getCurrentUser(): UserAccount | null {
    return this.storage.get<UserAccount>(keys.user);
  }

  setLogoutConfirmation(visible: boolean): void {
    this.logoutConfirmationSubject.next(visible);
  }

  startTracking(): void {
    if (this.tracking) {
      return;
    }

    this.tracking = true;
    this.zone.runOutsideAngular(() => {
      ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, this.onActivity, true));
    });
    this.resetTimer();
  }

  stopTracking(): void {
    this.clearTimers();
    if (!this.tracking) {
      return;
    }

    this.tracking = false;
    ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, this.onActivity, true));
  }

  resetTimer(): void {
    this.clearTimers();
    if (this.isDialogBoxActiveSubject.value) {
      this.zone.run(() => this.isDialogBoxActiveSubject.next(false));
    }

    this.zone.runOutsideAngular(() => {
      this.inactivityTimeout = setTimeout(
        () => this.raiseInactivityWarning(),
        environment.inactivity.warningAfterMs
      );
    });
  }

  continueSession(): void {
    this.isDialogBoxActiveSubject.next(false);
    this.resetTimer();
  }

  private raiseInactivityWarning(): void {
    this.zone.run(() => {
      this.isDialogBoxActiveSubject.next(true);
      this.startDialogTimer();
    });
  }

  private startDialogTimer(): void {
    this.zone.runOutsideAngular(() => {
      this.dialogTimeout = setTimeout(
        () => this.zone.run(() => this.logout()),
        environment.inactivity.logoutAfterMs
      );
    });
  }

  private clearTimers(): void {
    if (this.inactivityTimeout !== null) {
      clearTimeout(this.inactivityTimeout);
      this.inactivityTimeout = null;
    }
    if (this.dialogTimeout !== null) {
      clearTimeout(this.dialogTimeout);
      this.dialogTimeout = null;
    }
  }

  private setSession(response: LoginResponse): void {
    this.storeTokens(response);
    this.storage.set<UserAccount>(keys.user, response.user);
    this.permissionsService.setPermissions(toUserPermissions(response.permissions, response.roles));
    this.currentUserSubject.next(response.user);
  }

  private storeTokens(tokens: AuthTokens): void {
    this.storage.setRaw(keys.token, tokens.accessToken);
    this.storage.setRaw(keys.refreshToken, tokens.refreshToken);
  }

  private clearStoredSession(): void {
    const rememberedEmail = this.storage.getRaw(keys.rememberedEmail);
    const language = this.storage.getRaw(keys.language);

    this.storage.clear();

    if (rememberedEmail) {
      this.storage.setRaw(keys.rememberedEmail, rememberedEmail);
    }
    if (language) {
      this.storage.setRaw(keys.language, language);
    }
  }

  private url(path: string): string {
    return environment.apiUrl + path;
  }
}
